import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { isSystemAdmin } from './aivisRoles';
import { clearbitLogoFromWebsite } from '../../utils/clearbitLogo';
import './ai-vis.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const abs = Math.abs(seconds);
  if (abs < 60) return `${Math.round(abs)}s`;
  if (abs < 3600) return `${Math.floor(abs / 60)}m ${Math.round(abs % 60)}s`;
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatRelative(isoString) {
  if (!isoString) return '—';
  const diff = (new Date(isoString) - Date.now()) / 1000;
  const label = diff < 0 ? 'ago' : 'from now';
  return `${formatDuration(Math.abs(diff))} ${label}`;
}

function formatAbsolute(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Sort: active first (running → queued), then by next_run_seconds asc, nulls last ──

function sortCampaigns(list) {
  return [...list].sort((a, b) => {
    const priority = { running: 0, queued: 1, stale: 2, idle: 3 };
    const pa = priority[a.status] ?? 3;
    const pb = priority[b.status] ?? 3;
    if (pa !== pb) return pa - pb;
    // Both same priority — sort by next run ascending, nulls last
    if (a.next_run_seconds == null && b.next_run_seconds == null) return 0;
    if (a.next_run_seconds == null) return 1;
    if (b.next_run_seconds == null) return -1;
    return a.next_run_seconds - b.next_run_seconds;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ClientLogo({ logoUrl, domain, name, size = 28 }) {
  const [failed, setFailed] = useState(false);
  const src = (!failed && logoUrl) ? logoUrl : clearbitLogoFromWebsite(domain);
  const fallbackLetter = (name || '?').slice(0, 1).toUpperCase();

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        width={size} height={size}
        style={{ borderRadius: 6, objectFit: 'contain', flexShrink: 0, border: '1px solid var(--aivis-border)' }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: 6, background: 'var(--aivis-bg)',
      border: '1px solid var(--aivis-border)', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', fontSize: size * 0.45, fontWeight: 700,
      color: 'var(--aivis-text-muted)', flexShrink: 0,
    }}>
      {fallbackLetter}
    </span>
  );
}

function OrgLogo({ logoUrl, name, size = 20 }) {
  const [failed, setFailed] = useState(false);
  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl} alt="" width={size} height={size}
        style={{ borderRadius: 4, objectFit: 'contain', flexShrink: 0 }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: 4, background: 'var(--aivis-bg)',
      border: '1px solid var(--aivis-border)', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 10, fontWeight: 700,
      color: 'var(--aivis-text-muted)', flexShrink: 0,
    }}>
      {(name || '?').slice(0, 1).toUpperCase()}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    running: { label: 'Running', color: 'var(--aivis-positive-ink)', bg: 'var(--aivis-positive-soft)', dot: true },
    queued:  { label: 'Queued',  color: 'var(--aivis-caution-ink)', bg: 'var(--aivis-caution-soft)', dot: false },
    stale:   { label: 'Stale',   color: 'var(--aivis-danger-ink)', bg: 'var(--aivis-danger-soft)', dot: false },
    idle:    { label: 'Idle',    color: 'var(--aivis-text-muted)', bg: 'var(--aivis-bg)', dot: false },
  };
  const { label, color, bg, dot } = map[status] || map.idle;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 11.5, fontWeight: 600, background: bg, color, whiteSpace: 'nowrap' }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', animation: 'swo-pulse 1.4s ease-in-out infinite' }} />}
      {label}
    </span>
  );
}

function RunChip({ run }) {
  const map = {
    completed: { bg: 'var(--aivis-positive-soft)', border: 'var(--aivis-positive)' },
    failed:    { bg: 'var(--aivis-danger-soft)', border: 'var(--aivis-danger-soft)' },
    cancelled: { bg: 'var(--aivis-bg)', border: 'var(--aivis-border-strong)' },
    running:   { bg: 'var(--aivis-info-soft)', border: 'var(--aivis-info)' },
    queued:    { bg: 'var(--aivis-caution-soft)', border: 'var(--aivis-caution)' },
  };
  const { bg, border } = map[run.status] || map.cancelled;
  return (
    <span
      title={`${run.status} — ${formatAbsolute(run.started_at || run.scheduled_for)}`}
      style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 3, background: bg, border: `1.5px solid ${border}`, cursor: 'default', flexShrink: 0 }}
    />
  );
}

function ProgressBar({ done, total }) {
  if (!total) return <span style={{ color: 'var(--aivis-text-muted)', fontSize: 12 }}>—</span>;
  const pct = Math.round((done / total) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ flex: 1, height: 5, borderRadius: 99, background: 'var(--aivis-border)', overflow: 'hidden', minWidth: 50 }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: pct === 100 ? 'var(--aivis-positive)' : 'var(--aivis-accent)', transition: 'width 0.5s' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--aivis-text-muted)', whiteSpace: 'nowrap', minWidth: 36 }}>{done}/{total}</span>
    </div>
  );
}

function HeartbeatAge({ secs }) {
  if (secs == null) return null;
  const color = secs < 120 ? 'var(--aivis-positive)' : secs < 1800 ? 'var(--aivis-caution)' : 'var(--aivis-danger)';
  return (
    <span title={`Last heartbeat ${formatDuration(secs)} ago`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, color, marginTop: 3 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
      {formatDuration(secs)} ago
    </span>
  );
}

// ─── KPI stat card ────────────────────────────────────────────────────────────

function StatCard({ label, value, accent, loading }) {
  return (
    <div style={{ padding: '16px 20px', borderRadius: 12, border: '1px solid var(--aivis-border)', background: 'var(--aivis-panel)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: accent || 'var(--aivis-text)' }}>
        {loading && value === '—' ? <span style={{ fontSize: 16, color: 'var(--aivis-text-muted)' }}>…</span> : value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--aivis-text-muted)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

// ─── Run legend ───────────────────────────────────────────────────────────────

const RUN_LEGEND = [
  { label: 'Completed', bg: 'var(--aivis-positive-soft)', border: 'var(--aivis-positive)' },
  { label: 'Failed',    bg: 'var(--aivis-danger-soft)', border: 'var(--aivis-danger-soft)' },
  { label: 'Running',   bg: 'var(--aivis-info-soft)', border: 'var(--aivis-info)' },
  { label: 'Queued',    bg: 'var(--aivis-caution-soft)', border: 'var(--aivis-caution)' },
  { label: 'Cancelled', bg: 'var(--aivis-bg)', border: 'var(--aivis-border-strong)' },
];

// ─── Grid columns ─────────────────────────────────────────────────────────────
const COLS = '2fr 180px 100px 170px 170px 100px 110px';

// ─── Table ────────────────────────────────────────────────────────────────────

function TableHead() {
  const cols = [
    { label: 'Client' },
    { label: 'Organization' },
    { label: 'Status' },
    { label: 'Progress / Heartbeat' },
    { label: 'Next Run' },
    { label: 'Last Done' },
    { label: 'Last 5 Runs', hint: 'Each square = one run. Newest on the left.' },
  ];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: COLS, gap: 12,
      padding: '10px 20px', borderBottom: '1px solid var(--aivis-border)',
      background: 'var(--aivis-bg)',
    }}>
      {cols.map((col) => (
        <div key={col.label} title={col.hint} style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--aivis-text-muted)', whiteSpace: 'nowrap', cursor: col.hint ? 'help' : 'default' }}>
          {col.label}
          {col.hint && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, verticalAlign: 'middle', opacity: 0.55 }}>
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
          )}
        </div>
      ))}
    </div>
  );
}

function CampaignRow({ c, isLast }) {
  const ar = c.active_run;
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center',
        padding: '12px 20px', borderBottom: isLast ? 'none' : '1px solid var(--aivis-border)',
        fontSize: 13, transition: 'background 0.12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--aivis-bg)'}
      onMouseLeave={e => e.currentTarget.style.background = ''}
    >
      {/* Client — logo + name + domain */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <ClientLogo logoUrl={c.client_logo_url} domain={c.domain} name={c.name} size={30} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: 'var(--aivis-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.name}
          </div>
          {c.domain && (
            <div style={{ fontSize: 11, color: 'var(--aivis-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
              {c.domain.replace(/^https?:\/\/(www\.)?/, '')}
            </div>
          )}
        </div>
      </div>

      {/* Organization — logo only (name in tooltip) */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }} title={c.organization_name || ''}>
        <OrgLogo logoUrl={c.organization_logo_url} name={c.organization_name} size={22} />
      </div>

      {/* Status */}
      <div><StatusBadge status={c.status} /></div>

      {/* Progress + heartbeat */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <ProgressBar done={ar?.exec_done} total={ar?.exec_total} />
        <HeartbeatAge secs={ar?.heartbeat_age_seconds} />
      </div>

      {/* Next run */}
      <div style={{ fontSize: 12 }}>
        {c.status === 'running' || c.status === 'queued' ? (
          <span style={{ color: 'var(--aivis-accent)', fontWeight: 500 }}>In progress…</span>
        ) : c.next_run_at ? (
          <>
            <div style={{ fontWeight: 600, color: 'var(--aivis-text)' }}>
              {c.next_run_seconds != null && c.next_run_seconds > 0 ? formatDuration(c.next_run_seconds) : 'Soon'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--aivis-text-muted)', marginTop: 1 }}>{formatAbsolute(c.next_run_at)}</div>
          </>
        ) : <span style={{ color: 'var(--aivis-text-muted)' }}>—</span>}
      </div>

      {/* Last completed */}
      <div style={{ fontSize: 11, color: 'var(--aivis-text-muted)' }}>
        {c.last_run_completed_at ? formatRelative(c.last_run_completed_at) : '—'}
      </div>

      {/* Recent run chips */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {c.recent_runs.length > 0
          ? c.recent_runs.slice(0, 5).map((r) => <RunChip key={r.id} run={r} />)
          : <span style={{ fontSize: 11, color: 'var(--aivis-text-muted)' }}>—</span>}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SystemWideOverviewPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/aivis/system-overview/');
      setData(res.data);
      setLastFetched(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load system overview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  if (!isSystemAdmin(user)) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--aivis-text-muted)', fontSize: 14 }}>
        System admin access required.
      </div>
    );
  }

  const orgs    = data?.organizations ?? [];
  const summary = data?.summary ?? {};

  // Flatten all campaigns from all orgs into one sorted list
  const allCampaigns = sortCampaigns(
    orgs.flatMap((org) => org.campaigns)
  );

  return (
    <>
      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 className="aivis-page-title" style={{ marginBottom: 4 }}>System Overview</h1>
          <p className="aivis-page-sub" style={{ marginBottom: 0 }}>Live status across all organizations. Auto-refreshes every 30 s.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastFetched && (
            <span style={{ fontSize: 12, color: 'var(--aivis-text-muted)' }}>
              Updated {formatAbsolute(lastFetched)}
            </span>
          )}
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="aivis-btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto' }}
          >
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={loading ? { animation: 'swo-spin 1s linear infinite' } : {}}
            >
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
              <path d="M16 16h5v5" />
            </svg>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 28 }}>
        <StatCard label="Total Clients"   value={summary.total_campaigns ?? '—'} loading={loading} />
        <StatCard label="Active Runs"     value={summary.active_runs ?? '—'}     loading={loading} accent={summary.active_runs > 0 ? 'var(--aivis-info)' : undefined} />
        <StatCard label="Queued"          value={summary.queued_runs  ?? '—'}    loading={loading} accent={summary.queued_runs  > 0 ? 'var(--aivis-caution-ink)' : undefined} />
        <StatCard label="Stale Runs"      value={summary.stale_runs   ?? '—'}    loading={loading} accent={summary.stale_runs   > 0 ? 'var(--aivis-danger-ink)' : undefined} />
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--aivis-danger-soft)', color: 'var(--aivis-danger-ink)', fontSize: 13, marginBottom: 20, border: '1px solid #fca5a5' }}>
          {error}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--aivis-text-muted)', fontSize: 14 }}>
          Loading system overview…
        </div>
      )}

      {/* ── Main table ── */}
      {(!loading || data) && (
        allCampaigns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--aivis-text-muted)', fontSize: 14 }}>
            No clients found.
          </div>
        ) : (
          <div style={{ border: '1px solid var(--aivis-border)', borderRadius: 12, overflow: 'hidden', background: 'var(--aivis-panel)' }}>
            {/* Run legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 20px 7px', background: 'var(--aivis-bg)', borderBottom: '1px solid var(--aivis-border)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--aivis-text-muted)', marginRight: 2 }}>
                Run history:
              </span>
              {RUN_LEGEND.map(({ label, bg, border }) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--aivis-text-muted)' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                  {label}
                </span>
              ))}
              <span style={{ fontSize: 10.5, color: 'var(--aivis-text-muted)', marginLeft: 4 }}>
                (newest → oldest, hover for date)
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--aivis-text-muted)', marginLeft: 'auto' }}>
                Sorted by next run ↑
              </span>
            </div>

            <TableHead />

            {allCampaigns.map((c, i) => (
              <CampaignRow key={c.id} c={c} isLast={i === allCampaigns.length - 1} />
            ))}
          </div>
        )
      )}

      <style>{`
        @keyframes swo-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(0.7); }
        }
        @keyframes swo-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
